/**
 * External API Service
 *
 * This service routes external API requests through Titan's backend
 * which integrates with rfxcel's public API.
 * The backend handles authentication and data transformation.
 */

// Use Titan backend as proxy for external (rfxcel) API
const EXTERNAL_API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.REACT_APP_API_URL || 'http://localhost:3003/api/v1';

/**
 * Generic fetch wrapper for external API calls
 * Routes through Titan backend with source=external parameter
 */
const fetchExternalData = async (endpoint, options = {}) => {
  // Add source=external query parameter to route through rfxcel API
  const separator = endpoint.includes('?') ? '&' : '?';
  const url = `${EXTERNAL_API_BASE_URL}${endpoint}${separator}source=external`;

  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`External API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Backend already returns correct format
    return data.data;
  } catch (error) {
    console.error('External API fetch error:', error);
    throw new Error(`Failed to fetch from external API: ${error.message}`);
  }
};

/**
 * Dashboard Analytics Endpoints
 * These mirror the Titan backend endpoints but call external APIs
 */

export const getDashboardStats = async (timeRange = '24h') => {
  const data = await fetchExternalData(`/analytics/stats?timeRange=${timeRange}`);
  return data;
};

export const getShipmentFrequency = async (days = 30, granularity = 'daily') => {
  const data = await fetchExternalData(`/analytics/shipment-frequency?days=${days}&granularity=${granularity}`);
  return data;
};

export const getQuantityDistribution = async () => {
  const data = await fetchExternalData('/analytics/quantity-distribution');
  return data;
};

export const getProductTrends = async (days = 7) => {
  const data = await fetchExternalData(`/analytics/product-trends?days=${days}`);
  return data;
};

export const getLocationHeatmap = async () => {
  const data = await fetchExternalData('/analytics/location-heatmap');
  return data;
};

export const getExceptionAnalysis = async () => {
  const data = await fetchExternalData('/analytics/exception-analysis');
  return data;
};

export const getRecentShipments = async (limit = 10) => {
  const data = await fetchExternalData(`/analytics/recent-shipments?limit=${limit}`);
  return data;
};

export const getTopProducts = async (limit = 10) => {
  const data = await fetchExternalData(`/analytics/top-products?limit=${limit}`);
  return data;
};

export const getTopLocations = async (limit = 10) => {
  const data = await fetchExternalData(`/analytics/top-locations?limit=${limit}`);
  return data;
};

export const getStatusBreakdown = async () => {
  const data = await fetchExternalData('/analytics/status-breakdown');
  return data;
};

export const getTimelineData = async (days = 30) => {
  const data = await fetchExternalData(`/analytics/timeline?days=${days}`);
  return data;
};

export const getPerformanceMetrics = async () => {
  const data = await fetchExternalData('/analytics/performance-metrics');
  return data;
};

/**
 * Fetch all dashboard data in parallel from external API
 * Returns the same structure as Titan's fetchAllDashboardData
 */
export const fetchAllExternalDashboardData = async () => {
  try {
    const [
      stats,
      shipmentFrequency,
      quantityDistribution,
      productTrends,
      locationHeatmap,
      exceptionAnalysis,
      recentShipments,
      topProducts,
      topLocations,
      statusBreakdown,
      timelineData,
      performanceMetrics
    ] = await Promise.all([
      getDashboardStats('24h').catch(err => {
        console.error('External API - Stats error:', err);
        return null;
      }),
      getShipmentFrequency(30, 'daily').catch(err => {
        console.error('External API - Shipment frequency error:', err);
        return null;
      }),
      getQuantityDistribution().catch(err => {
        console.error('External API - Quantity distribution error:', err);
        return null;
      }),
      getProductTrends(7).catch(err => {
        console.error('External API - Product trends error:', err);
        return null;
      }),
      getLocationHeatmap().catch(err => {
        console.error('External API - Location heatmap error:', err);
        return null;
      }),
      getExceptionAnalysis().catch(err => {
        console.error('External API - Exception analysis error:', err);
        return null;
      }),
      getRecentShipments(10).catch(err => {
        console.error('External API - Recent shipments error:', err);
        return null;
      }),
      getTopProducts(10).catch(err => {
        console.error('External API - Top products error:', err);
        return null;
      }),
      getTopLocations(10).catch(err => {
        console.error('External API - Top locations error:', err);
        return null;
      }),
      getStatusBreakdown().catch(err => {
        console.error('External API - Status breakdown error:', err);
        return null;
      }),
      getTimelineData(30).catch(err => {
        console.error('External API - Timeline data error:', err);
        return null;
      }),
      getPerformanceMetrics().catch(err => {
        console.error('External API - Performance metrics error:', err);
        return null;
      })
    ]);

    return {
      stats,
      shipmentFrequency,
      quantityDistribution,
      productTrends,
      locationHeatmap,
      exceptionAnalysis,
      recentShipments,
      topProducts,
      topLocations,
      statusBreakdown,
      timelineData,
      performanceMetrics,
      source: 'external'
    };
  } catch (error) {
    console.error('Error fetching external dashboard data:', error);
    throw error;
  }
};

export default {
  getDashboardStats,
  getShipmentFrequency,
  getQuantityDistribution,
  getProductTrends,
  getLocationHeatmap,
  getExceptionAnalysis,
  getRecentShipments,
  getTopProducts,
  getTopLocations,
  getStatusBreakdown,
  getTimelineData,
  getPerformanceMetrics,
  fetchAllExternalDashboardData
};
